import React, { useState } from "react";
import { Col, Form, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";

import classNames from "classnames";

import { useAppSelector } from "../../hooks/reduxHooks";
import useUserSwitcher from "../../hooks/useUserSwitcher";
import { TEAM_LOGO_URL } from "../../lib/globals";
import { defaultDateFormat } from "../../lib/utils";

interface Filters {
    search: string | undefined;
    ageLevels: {
        options: Array<string>;
        filter: Array<string> | undefined;
    };
    tournaments: {
        options: Array<{ value: number; label: string }>;
        filter: Array<{ value: number; label: string }> | undefined;
    };
}

const AdminTable = (): React.JSX.Element => {
    const { tournaments } = useAppSelector(state => state.clubhouse);
    const { masterTournaments } = useAppSelector(
        state => state.tournamentmanagement,
    );
    const { user } = useAppSelector(state => state.auth);
    const { handleUserSwitch } = useUserSwitcher();

    const [filters, setFilters] = useState<Filters>({
        search: undefined,
        ageLevels: {
            options: [],
            filter: undefined,
        },
        tournaments: {
            options: masterTournaments.map(t => {
                return {
                    value: t.id,
                    label: `${t.name} ${t.startDate && defaultDateFormat.format(new Date(t.startDate))} - ${t.endDate && defaultDateFormat.format(new Date(t.endDate))}`,
                };
            }) as Array<{ value: number; label: string }>,
            filter: undefined,
        },
    });

    return (
        <>
            <div className="clubhouseTeamFilters">
                <Row>
                    <Col xs={6} className="py-2 py-sm-0">
                        <h5>Tournament:</h5>
                        <Select
                            isSearchable={true}
                            isClearable={true}
                            isMulti={true}
                            options={filters.tournaments.options}
                            onChange={t => {
                                setFilters(prev => ({
                                    ...prev,
                                    tournaments: {
                                        ...prev.tournaments,
                                        filter:
                                            t && t.length === 0
                                                ? undefined
                                                : (t as {
                                                      value: number;
                                                      label: string;
                                                  }[]),
                                    },
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={6} lg={4} className="py-2 py-sm-0">
                        <h5>Search:</h5>
                        <Form.Control
                            type="text"
                            placeholder="Enter search value..."
                            onChange={e => {
                                setFilters(prev => ({
                                    ...prev,
                                    search:
                                        e.target.value.trim() === ""
                                            ? undefined
                                            : e.target.value.trim(),
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={6} lg={2} className="py-2 py-sm-0">
                        <h5>Age Level:</h5>
                        <Select
                            isSearchable={false}
                            isClearable={true}
                            isMulti={true}
                            options={filters.ageLevels.options.map((a, idx) => {
                                return {
                                    value: idx,
                                    label: a,
                                };
                            })}
                            onChange={a => {
                                setFilters(prev => ({
                                    ...prev,
                                    ageLevels: {
                                        ...prev.ageLevels,
                                        filter:
                                            a.length === 0
                                                ? undefined
                                                : a?.map(o => o.label),
                                    },
                                }));
                            }}
                        />
                    </Col>
                </Row>
            </div>

            <Table className="clubhouseTable" striped bordered responsive>
                <thead>
                    <tr>
                        <th>Age Level</th>
                        <th>Logo</th>
                        <th>Team</th>
                        <th>Coach Name</th>
                        <th>Coach Email</th>
                        <th>Coach Phone</th>
                        <th>Roster</th>
                        <th>COI</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {tournaments
                        .filter(t =>
                            filters.search
                                ? t.teams.some(
                                      team =>
                                          filters.search &&
                                          team.name
                                              .toLowerCase()
                                              .includes(
                                                  filters.search.toLowerCase(),
                                              ),
                                  ) ||
                                  t.teams.some(
                                      t =>
                                          filters.search &&
                                          t.coach.firstName
                                              .toLowerCase()
                                              .includes(
                                                  filters.search.toLowerCase(),
                                              ),
                                  ) ||
                                  t.teams.some(
                                      t =>
                                          filters.search &&
                                          t.coach.lastName
                                              .toLowerCase()
                                              .includes(
                                                  filters.search.toLowerCase(),
                                              ),
                                  ) ||
                                  t.teams.some(
                                      t =>
                                          filters.search &&
                                          t.coach.email
                                              .toLowerCase()
                                              .includes(
                                                  filters.search.toLowerCase(),
                                              ),
                                  )
                                : true,
                        )
                        .filter(t =>
                            filters.ageLevels.filter
                                ? filters.ageLevels.filter.includes(t.ageLevel)
                                : true,
                        )
                        .filter(t =>
                            filters.tournaments.filter
                                ? filters.tournaments.filter.some(
                                      f => f.value === t.parent,
                                  )
                                : true,
                        )
                        .sort((a, b) => {
                            const dateCompare = a.startDate.localeCompare(
                                b.startDate,
                            );

                            if (dateCompare !== 0) {
                                return dateCompare;
                            } else {
                                const nameCompare = a.name.localeCompare(
                                    b.name,
                                );

                                if (nameCompare !== 0) {
                                    return nameCompare;
                                } else {
                                    const aAge = parseInt(a.ageLevel);
                                    const bAge = parseInt(b.ageLevel);
                                    return aAge - bAge;
                                }
                            }
                        })
                        .map(t => (
                            <React.Fragment key={`${t.id}`}>
                                <tr>
                                    <td colSpan={9} className="tourneyName">
                                        <h5>{`${t.name} - ${t.ageLevel} - ${t.startDate && defaultDateFormat.format(new Date(t.startDate))}-${t.endDate && defaultDateFormat.format(new Date(t.endDate))}`}</h5>
                                    </td>
                                </tr>
                                {t.teams
                                    .filter(t =>
                                        filters.search
                                            ? t.name
                                                  .toLowerCase()
                                                  .includes(
                                                      filters.search.toLowerCase(),
                                                  ) ||
                                              t.coach.email
                                                  .toLowerCase()
                                                  .includes(
                                                      filters.search.toLowerCase(),
                                                  ) ||
                                              t.coach.firstName
                                                  .toLowerCase()
                                                  .includes(
                                                      filters.search.toLowerCase(),
                                                  ) ||
                                              t.coach.lastName
                                                  .toLowerCase()
                                                  .includes(
                                                      filters.search.toLowerCase(),
                                                  ) ||
                                              t.coach.phone
                                                  ?.toLowerCase()
                                                  .includes(
                                                      filters.search.toLowerCase(),
                                                  )
                                            : true,
                                    )
                                    .map(team => (
                                        <tr key={`${t.id}-${team.id}`}>
                                            <td>{t.ageLevel}</td>
                                            <td>
                                                {team.logo && (
                                                    <img
                                                        src={`${TEAM_LOGO_URL}${team.logo}`}
                                                        alt={`${team.name} logo`}
                                                        className="teamLogo img-fluid"
                                                    />
                                                )}
                                            </td>
                                            <td>
                                                <Link
                                                    to={`/coachs-clubhouse/team/${team.id}`}
                                                >
                                                    <h5 className="teamLink">
                                                        {team.name}
                                                    </h5>
                                                </Link>
                                            </td>
                                            <td>{`${team.coach.firstName} ${team.coach.lastName}`}</td>
                                            <td>
                                                <a
                                                    href={`mailto:${team.coach.email}`}
                                                >
                                                    {team.coach.email}
                                                </a>
                                            </td>
                                            <td>{team.coach.phone}</td>
                                            <td></td>
                                            <td></td>
                                            <td>
                                                <span
                                                    className={classNames(
                                                        "switchUserLink",
                                                    )}
                                                    onClick={() =>
                                                        handleUserSwitch(
                                                            team.coach.id,
                                                        )
                                                    }
                                                >
                                                    switch to user
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                            </React.Fragment>
                        ))}
                </tbody>
            </Table>
        </>
    );
};

export default AdminTable;
